/**
 * TerrainFarTierModule — the ground to the horizon (HLOD-1 / HLOD-0 for
 * TERRAIN, 2026-09-03: "if I am in the sky ship looking down I should see
 * terrain (low quality) as far as I can see, even on potato, without
 * impacting performance").
 *
 * Four nested coarse grids (lib/terrain_far_tier.ts — geometry clipmaps:
 * constant vertex budget, ~57 k triangles, four draw calls) ride the player:
 * level A (64 m cells, ±3 km) lies UNDER the streamed chunk window sampled
 * conservatively low, B/C/D carry the ground out to ±98 km. Each level
 * re-centres on its own snapped grid when the player drifts two snaps; the
 * heights come from a worker (the terrain kernel when it lands), the mesh
 * wears the chunks' own biome ground material through world-space UVs and the
 * worker's packed weights (a vertex tint stands in until the ground textures
 * land), fogged, unshadowed. A coarser
 * level's annulus hole is re-indexed the moment the level inside it moves.
 *
 * Two side effects the tier needs: the ground camera's far plane extends to
 * GROUND_FAR_PLANE_M (the 5.2 km plane clipped everything beyond), and the
 * scene fog thins with the viewer's height above ground (aerial
 * perspective — `farTierFog.factor`, multiplied in by AtmosphereModule).
 *
 * Diag: `__farTerrain()`. Opt out: localStorage `atlas:far-terrain` = 'off'.
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { Phase, makeSystem } from '../engine-compat.js';
import { atlas } from '../state.js';
import { postRiverTable } from '../lib/hydrology_load.js';
import { bus } from '../bus.js';
import { disposeLater } from '../lib/gpu_dispose.js';
import { distantDetailOn } from '../lib/quality_tiers.js';
import { meshHeight } from '../lib/terrain_stamp.js';
import { commonsSeaLevel } from '../lib/sea_level.js';
import { FAR_LEVELS, snapCentre, needsRecentre, gridOrigin, annulusIndices, holeFor, farUv, GROUND_FAR_PLANE_M, fogAltitudeFactor, farTierFog, farTierBudget, farTierReach, FAR_SEA_RGB, type FarLevel, type Hole } from '../lib/terrain_far_tier.js';
import type { FarJob, FarResult } from '../workers/terrain_far_worker.js';
import type { EnvironmentModule } from './EnvironmentModule.js';
// Water agent (R5, 2026-09-25): the far sea wears the near sea's water in far mode (lib/water_look.ts).
import { currentFarSea } from '../lib/water_look.js';
// ── ground v2 (ground agent, 2026-09-25): R7 — per-level baked normal map + macro albedo ──
import { applyGroundFar, type FarGroundHandles } from '../lib/wetness.js';
import { applyCloudShadows } from '../lib/cloud_shadows.js';
import { activePreset } from '../lib/quality_tiers.js';
import type { FarGroundBake } from '../lib/ground_far.js';
// ── end ground v2 ──

const TICK_EVERY = 6;

interface LevelState {
  l: FarLevel;
  mesh: THREE.Mesh | null;
  /** Per-vertex sea flags of the BUILT grid (the land/sea index split). */
  sea: Uint8Array | null;
  /** Snapped centre of the BUILT grid (NaN until built). */
  cx: number; cz: number;
  /** Centre requested from the worker (in flight), NaN when idle. */
  reqCx: number; reqCz: number; reqId: number;
  /** The hole the built index carries (the inner level's centre it was cut for). */
  holeCx: number; holeCz: number;
  builds: number;
  // ── ground v2 (R7): the level's own land material over its baked textures ──
  groundBuilt: 'v1' | 'v2';
  farMat: THREE.MeshStandardMaterial | null;
  farTex: { albedo: THREE.DataTexture; normal: THREE.DataTexture } | null;
  farHandles: FarGroundHandles | null;
  farMacro: THREE.Texture | null;
}

export class TerrainFarTierModule implements IModule {
  readonly name = 'TerrainFarTier';
  private scene: THREE.Scene | null = null;
  private worker: Worker | null = null;
  private nextId = 1;
  private frame = 0;
  private seed = '';
  private seaLevel = 0;
  private readonly levels: LevelState[] = FAR_LEVELS.map((l) => ({ l, mesh: null, sea: null, cx: NaN, cz: NaN, reqCx: NaN, reqCz: NaN, reqId: 0, holeCx: NaN, holeCz: NaN, builds: 0, groundBuilt: 'v1', farMat: null, farTex: null, farHandles: null, farMacro: null }));
  private app: App | null = null;
  /** Land before the ground textures land: a vertex-tinted fallback (the biome material replaces it the tick it exists). */
  private readonly fallbackMat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 1, metalness: 0, fog: true });
  /** The far sea: flat, water-coloured, a touch glossy, fogged — the water module's plane only reaches 3 km. */
  private readonly seaMat = new THREE.MeshStandardMaterial({ color: new THREE.Color(FAR_SEA_RGB[0], FAR_SEA_RGB[1], FAR_SEA_RGB[2]), roughness: 0.35, metalness: 0.05, fog: true });
  private readonly stats = { builds: 0, workerMs: 0, lastWorkerMs: 0, reindexes: 0, fogFactor: 1, agl: 0, farPlane: 0, enabled: true, textured: false, seaTris: 0, ground: 'v1' as 'v1' | 'v2', bakeMs: 0 };
  private farPlaneApplied = false;

  constructor(private readonly environment: EnvironmentModule) {}

  /** TEXTURE, NOT A PLAIN COLOUR (2026-09-03): the far tier wears the SAME biome
   *  ground material as the streamed chunks — six blended ground albedos, the
   *  70 m+ re-tile, the macro noise, the shoreline band — through world-space
   *  UVs and the packed biome weights the worker computes. Until its textures
   *  have landed the vertex-tinted fallback stands in. */
  /** Water agent (R5): the realistic water in far mode when the tier has it, else the lit standard sheet. */
  private seaMaterial(): THREE.Material { return currentFarSea()?.material ?? this.seaMat; }

  private landMaterial(s?: LevelState): THREE.Material {
    // ── ground v2 (R7): the level wears its OWN material over its baked normal map + macro albedo ──
    if (this.environment.groundVersion === 'v2') {
      this.stats.textured = !!s?.farMat;
      return s?.farMat ?? this.fallbackMat;
    }
    // ── end ground v2 ──
    const m = this.environment.biomeMaterial;
    this.stats.textured = !!m;
    return m ?? this.fallbackMat;
  }

  // ── ground v2 (R7): install a level's bake — DataTextures + (once) the level's material ──
  private installBake(s: LevelState, bake: FarGroundBake): void {
    const mk = (data: Uint8Array, srgb: boolean): THREE.DataTexture => {
      const t = new THREE.DataTexture(data, bake.texN, bake.texN, THREE.RGBAFormat, THREE.UnsignedByteType);
      t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
      t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
      t.magFilter = THREE.LinearFilter; t.minFilter = THREE.LinearMipmapLinearFilter; t.generateMipmaps = true; t.flipY = false;
      t.needsUpdate = true;
      return t;
    };
    const albedo = mk(bake.albedo, true), normal = mk(bake.normal, false);
    const grid = new THREE.Vector4(bake.x0, bake.z0, bake.step, bake.texN);
    const macro = this.environment.groundMacro;
    if (!s.farMat || s.farMacro !== macro) {
      if (s.farMat) disposeLater(s.farMat);
      const mat = new THREE.MeshStandardMaterial({ roughness: 1, metalness: 0, fog: true });
      mat.name = `far-ground-v2-${s.l.id}`;
      s.farHandles = applyGroundFar(mat, albedo, normal, grid, macro);
      if (activePreset().cloudShadows && this.app) applyCloudShadows(this.app, mat as never);
      s.farMat = mat; s.farMacro = macro;
    } else if (s.farHandles) {
      // Same shader, new texel grid: swap the textures + the grid uniform (no pipeline rebuild).
      s.farHandles.albedo.value = albedo; s.farHandles.normal.value = normal; s.farHandles.grid.value.copy(grid);
    }
    if (s.farTex) { disposeLater(s.farTex.albedo); disposeLater(s.farTex.normal); }
    s.farTex = { albedo, normal };
    this.stats.bakeMs = bake.ms;
  }
  // ── end ground v2 ──

  init(app: App): void {
    this.scene = app.world.scene;
    this.app = app;
    this.stats.enabled = !optedOut();
    this.fallbackMat.userData.shared = true; // module-lifetime materials — never disposed with a level
    this.seaMat.userData.shared = true;
    app.world.addSystem(makeSystem('TerrainFarTier', Phase.RenderSub, 8, () => this.tick()));
    bus.on('atlas:world-entered', () => this.reset());
    (window as unknown as Record<string, unknown>).__farTerrain = () => ({
      ...this.stats,
      reach: farTierReach(),
      budget: farTierBudget(),
      levels: this.levels.map((s) => ({ id: s.l.id, built: !!s.mesh, cx: s.cx, cz: s.cz, inFlight: Number.isFinite(s.reqCx), builds: s.builds, tris: s.mesh ? (s.mesh.geometry.index?.count ?? 0) / 3 : 0, seaTris: s.mesh ? (s.mesh.geometry.groups[1]?.count ?? 0) / 3 : 0, visible: s.mesh?.visible ?? false, groundBuilt: s.groundBuilt })),
    });
  }

  private reset(): void {
    for (const s of this.levels) {
      if (s.mesh) { this.scene?.remove(s.mesh); disposeLater(s.mesh.geometry); s.mesh = null; s.sea = null; }
      s.cx = s.cz = s.reqCx = s.reqCz = s.holeCx = s.holeCz = NaN; s.reqId = 0;
    }
    this.seed = '';
  }

  private ensureWorker(): Worker | null {
    if (this.worker) return this.worker;
    try {
      this.worker = new Worker(new URL('../workers/terrain_far_worker.ts', import.meta.url), { type: 'module' });
      this.worker.onmessage = (e: MessageEvent<FarResult>) => this.onResult(e.data);
      if (this.seed) postRiverTable(this.worker, this.seed); // shared network (perf frontier item 12)
      this.worker.onerror = (e) => { console.warn('[TerrainFarTier] worker error', e.message); };
    } catch { this.worker = null; }
    return this.worker;
  }

  private optAt = -1e9;
  private opted = false;
  private tick(): void {
    // Distant detail OFF (the graphics toggle, 2026-09-08) = the kill switch,
    // live: the levels go and the fog stops thinning with altitude.
    const nowMs = performance.now();
    if (nowMs - this.optAt > 1000) { this.optAt = nowMs; this.opted = optedOut(); } // localStorage + a RegExp per FRAME before the tick gate (2026-09-12)
    const on = !this.opted && distantDetailOn();
    this.stats.enabled = on;
    if (!on) { if (this.levels.some((s) => s.mesh)) this.reset(); farTierFog.factor = 1; return; }
    const conn = atlas.conn;
    if (!conn || atlas.currentWorldId === null || atlas.currentWorldId !== atlas.commonsWorldId) {
      if (this.levels.some((s) => s.mesh)) this.reset();
      farTierFog.factor = 1;
      return;
    }
    if (!this.seed) {
      for (const w of conn.db.world.iter()) if (w.kind === 'commons') { this.seed = w.terrainSeed; break; }
      if (!this.seed) return;
      this.seaLevel = commonsSeaLevel(this.seed);
    }
    // The far plane: once (and again after anything resets it — cheap to re-assert).
    const cam = this.mainCamera();
    if (cam && cam.far < GROUND_FAR_PLANE_M && !atlas.spaceFlight) { cam.far = GROUND_FAR_PLANE_M; cam.updateProjectionMatrix(); this.farPlaneApplied = true; }
    this.stats.farPlane = cam?.far ?? 0;
    // Aerial perspective: thin the fog with height above ground.
    const agl = atlas.localY - meshHeight(this.seed, atlas.localX, atlas.localZ);
    this.stats.agl = agl;
    farTierFog.factor = fogAltitudeFactor(agl);
    this.stats.fogFactor = farTierFog.factor;
    if (++this.frame % TICK_EVERY !== 0) return;
    // The ground textures landed since a level was built: dress it.
    for (const s of this.levels) { const land = this.landMaterial(s); const mats = s.mesh?.material as THREE.Material[] | undefined; if (mats && mats[0] !== land) mats[0] = land; }
    // ── ground v2: a level built for the other ground version (a v1 ↔ v2 flip) rebuilds in place ──
    const gv = this.environment.groundVersion;
    this.stats.ground = gv;
    for (let i = 0; i < this.levels.length; i++) { const s = this.levels[i]; if (s.mesh && s.groundBuilt !== gv && !Number.isFinite(s.reqCx)) this.request(i, s.cx, s.cz); }
    // ── end ground v2 ──
    const sea = this.seaMaterial(); // water agent (R5): the far sea follows the water look
    for (const s of this.levels) { const mats = s.mesh?.material as THREE.Material[] | undefined; if (mats && mats[1] !== sea) mats[1] = sea; }
    const px = atlas.localX, pz = atlas.localZ;
    for (let i = 0; i < this.levels.length; i++) {
      const s = this.levels[i];
      const built = Number.isFinite(s.cx);
      const inFlight = Number.isFinite(s.reqCx);
      if (!inFlight && (!built || needsRecentre(s.l, px, pz, s.cx, s.cz))) {
        const c = snapCentre(s.l, px, pz);
        if (!built || c.x !== s.cx || c.z !== s.cz) this.request(i, c.x, c.z);
      }
      // The hole follows the inner level's BUILT centre — re-index when it moved.
      if (i > 0 && s.mesh) {
        const inner = this.levels[i - 1];
        if (Number.isFinite(inner.cx) && (inner.cx !== s.holeCx || inner.cz !== s.holeCz)) this.reindex(s, inner);
      }
    }
  }

  private request(level: number, cx: number, cz: number): void {
    const w = this.ensureWorker();
    if (!w) return;
    const s = this.levels[level];
    s.reqCx = cx; s.reqCz = cz; s.reqId = this.nextId++;
    const job: FarJob = { id: s.reqId, seed: this.seed, level, cx, cz, seaLevel: this.seaLevel, ground: this.environment.groundVersion };
    // ── canopy (vegetation agent, 2026-09-25, R4a): the tier's "Woods on the far terrain" switch reaches the worker
    // (it reads `canopy`, default on); a toggle applies at the next re-centre of each level ──
    (job as FarJob & { canopy?: boolean }).canopy = activePreset().farCanopy !== false;
    // ── end canopy ──
    w.postMessage(job);
  }

  private onResult(r: FarResult): void {
    const s = this.levels[r.level];
    if (!s || r.id !== s.reqId) return; // superseded
    s.reqCx = s.reqCz = NaN;
    if (!this.scene || !this.seed) return;
    const l = s.l, n = l.n;
    const { x0, z0 } = gridOrigin(l, r.cx, r.cz);
    const pos = new Float32Array(n * n * 3);
    const uv = new Float32Array(n * n * 2);
    for (let row = 0; row < n; row++) for (let col = 0; col < n; col++) {
      const i = row * n + col;
      const x = x0 + col * l.spacing, z = z0 + row * l.spacing;
      pos[i * 3] = x; pos[i * 3 + 1] = r.heights[i]; pos[i * 3 + 2] = z;
      const [u, v] = farUv(x, z); uv[i * 2] = u; uv[i * 2 + 1] = v;
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
    geo.setAttribute('color', new THREE.BufferAttribute(r.colors, 3));
    // The ground shader's vertex inputs — the chunk geometry carries the same set.
    // WHY every one, even the zero-filled channels: a TSL attribute() the geometry
    // lacks is not a pipeline error, it warns once and reads a ZERO CONSTANT, so a
    // missing biomeWA/WB would zero all six blend weights and paint the tier black
    // (roadW/fieldW zeroed on purpose — the far tier has no roads or fields).
    geo.setAttribute('biomeWA', new THREE.BufferAttribute(r.biomeWA, 3));
    geo.setAttribute('biomeWB', new THREE.BufferAttribute(r.biomeWB, 3));
    geo.setAttribute('roadW', new THREE.BufferAttribute(new Float32Array(n * n), 1));
    geo.setAttribute('fieldW', new THREE.BufferAttribute(new Float32Array(n * n), 1));
    const inner = r.level > 0 ? this.levels[r.level - 1] : null;
    const hole = inner && Number.isFinite(inner.cx) ? holeFor(inner.l, inner.cx, inner.cz) : null;
    this.setIndexGroups(geo, l, x0, z0, hole, r.sea);
    geo.computeVertexNormals();
    geo.computeBoundingSphere();
    // ── ground v2 (R7): the level's bake → its own land material ──
    s.groundBuilt = r.groundBake ? 'v2' : 'v1';
    if (r.groundBake) this.installBake(s, r.groundBake);
    // ── end ground v2 ──
    const mesh = new THREE.Mesh(geo, [this.landMaterial(s), this.seaMaterial()]);
    mesh.name = `far-terrain-${l.id}`;
    mesh.receiveShadow = false; mesh.castShadow = false;
    mesh.frustumCulled = true;
    mesh.renderOrder = -10; // under everything — the sky dome excepted
    const old = s.mesh;
    this.scene.add(mesh);
    if (old) { this.scene.remove(old); disposeLater(old.geometry); }
    s.mesh = mesh; s.sea = r.sea; s.cx = r.cx; s.cz = r.cz; s.builds++;
    s.holeCx = hole ? inner!.cx : NaN; s.holeCz = hole ? inner!.cz : NaN;
    this.stats.builds++; this.stats.workerMs += r.ms; this.stats.lastWorkerMs = r.ms;
    // The level OUTSIDE this one cuts its hole for us — refresh it now.
    const outer = this.levels[r.level + 1];
    if (outer?.mesh) this.reindex(outer, s);
  }

  /** Re-cut a level's annulus for the inner level's current centre (index only). */
  private reindex(s: LevelState, inner: LevelState): void {
    if (!s.mesh || !Number.isFinite(s.cx) || !Number.isFinite(inner.cx)) return;
    const { x0, z0 } = gridOrigin(s.l, s.cx, s.cz);
    this.setIndexGroups(s.mesh.geometry, s.l, x0, z0, holeFor(inner.l, inner.cx, inner.cz), s.sea);
    s.holeCx = inner.cx; s.holeCz = inner.cz;
    this.stats.reindexes++;
  }

  /** One index buffer, two draw groups: land cells (the biome ground material) then all-sea cells (the far sea). */
  private setIndexGroups(geo: THREE.BufferGeometry, l: FarLevel, x0: number, z0: number, hole: Hole | null, sea: Uint8Array | null): void {
    const land = annulusIndices(l, x0, z0, hole, sea, 'land');
    const seaIdx = sea ? annulusIndices(l, x0, z0, hole, sea, 'sea') : new Uint32Array(0);
    const all = new Uint32Array(land.length + seaIdx.length);
    all.set(land, 0); all.set(seaIdx, land.length);
    geo.setIndex(new THREE.BufferAttribute(all, 1));
    geo.clearGroups();
    geo.addGroup(0, land.length, 0);
    geo.addGroup(land.length, seaIdx.length, 1);
    this.stats.seaTris = this.levels.reduce((t, s) => t + (s.mesh ? (s.mesh.geometry.groups[1]?.count ?? 0) / 3 : 0), 0);
  }

  dispose(): void {
    this.reset();
    // ── ground v2: the levels' own materials + baked textures ──
    for (const s of this.levels) { if (s.farMat) disposeLater(s.farMat); if (s.farTex) { disposeLater(s.farTex.albedo); disposeLater(s.farTex.normal); } s.farMat = null; s.farTex = null; s.farHandles = null; }
    this.worker?.terminate(); this.worker = null;
    farTierFog.factor = 1;
    if (this.farPlaneApplied) { const cam = this.mainCamera(); if (cam) { cam.far = 5200; cam.updateProjectionMatrix(); } }
  }

  /** The ground camera rides the local rig as its first child (AtlasAvatarModule). */
  private mainCamera(): THREE.PerspectiveCamera | null {
    const c = atlas.localMesh?.children[0] as THREE.PerspectiveCamera | undefined;
    return c && (c as { isPerspectiveCamera?: boolean }).isPerspectiveCamera ? c : null;
  }
}

function optedOut(): boolean {
  try { return localStorage.getItem('atlas:far-terrain') === 'off' || /[?&]farTerrain=off\b/.test(location.search); } catch { return false; }
}
